import styles from "./Catalog.module.css"
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Navbar from "../../components/Navbar/NavbarHome/Navbar";
import Footer from "../../components/Footer/Footer";
import OnboardingPopup from "../../components/Popups/Onboarding/Onboarding";
import Filter from "../../components/Filter/Filter";

import HeroCarousel from "../../components/Sections/Landing/HeroCarousel";
import GallerySection from "../../components/Sections/Landing/GallerySection";
import LandingOffersSection from "../../components/Sections/Landing/LandingOffersSection";
import PartnersSection from "../../components/Sections/Landing/PartnersSection";
import ProductSection from "./components/ProductSection/ProductSection";

import { heroSlides, filterCardConfigs, HeroAction } from "../../constants/landingSections";
import useCatalogData from "../../hooks/useCatalogData";
import { useCmsContent } from "../../hooks/useCmsContent";

import { useFilterContext } from "../../context/FilterContext";
import { useOnboardingData } from "../../hooks/useOnboardingData";
import { useProductLayout } from "../../hooks/useProductLayout";
import { useTimeModal } from "../../context/TimeModalContext";
import useEnsureCatalogData from "../../hooks/useEnsureCatalogData";
import { Trans } from "react-i18next";
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import { usePublicCatalog } from "../../context/PublicCatalogContext";

// sample data
import { blogPosts } from "../../constants/blogPosts";
import BlogCard from "../../components/Cards/BlogCard/BlogCard";

import { log } from "../../utils/logger";

/**
* Home page
* Contains hero carousel, Image gallery, Product lists, Companys partners, Activities.
* @returns TSX.Element
*/
const Catalog = () => {
    // https://catermat-eshop-475202341553.europe-west4.run.app/public-catalog?uuid=77cdadcf-5689-46c0-8572-78a0e3048e05
    // -----------Handle uuid--------
    const {uuid, setUuid, isPublicView, setIsPublicView} = usePublicCatalog();
    const { stage } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams(); 
    
    useEffect(() => {
        const hasUUID = !!searchParams.get('uuid');
        if (hasUUID) {
            //log("Current uuid parameter: " + searchParams.get('uuid'))
            setUuid(searchParams.get('uuid') ?? "")
            if(searchParams.get('uuid') !== null && searchParams.get('uuid') !== ""){
                setIsPublicView(true)
            }
        }else  {
            //log("uuid parameter missing.")
        }
    }, [stage, searchParams, location.pathname]);
    // -----------Handle uuid--------
    
    const {
        products,
        newProducts,
        items,
        activities,
        loading,
        reloadProducts,
        reloadItems,
        reloadActivities,
    } = useEnsureCatalogData({
        productSource: "products",
    });
    // Reload items and products at the start
    useEffect(()=>{
        if(isPublicView){
            reloadProducts();
            reloadItems();
            reloadActivities();
        }
    },[uuid, isPublicView])
    // Reload cart at the start

    
    const { filterData, handleFilter, basicFilter, handleBasicFilter } = useFilterContext(); // Filter data
    const { productLayout, setProductLayout } = useProductLayout(); // Product layout
    const { open } = useTimeModal(); // Open time modal
    const {clearData} = useOnboardingData(); // Clear onboarding data
    const navigate = useNavigate(); // Navigate
    // Handle opening and closing the filter menu
    const [filterOpen, setFilterOpen] = useState(false)
    // Handle toggling onboarding popup
    const [onboardingOpen, setOnboardingOpen] = useState<boolean>(false);

    const handleHeroAction = (action: HeroAction) => {
        if (action === "about") {
            navigate("/about");
            return;
        }
        if (action === "onboarding") {
            setOnboardingOpen(true);
        }
    };

    const catalogData = useCatalogData({
        products,
        newProducts,
        items,
        activities,
    });

    const cmsContent = useCmsContent(); // CMS managed texts (undefined = use built-in fallbacks)

    return(
        <>
        <div className={styles.Page}>
            <div className={styles.Header}>
                <Navbar/>
            </div>

                <div className={styles.Content}>
                    <Filter
                    open={filterOpen}
                    setOpen={()=>setFilterOpen(!filterOpen)}
                    />
                    <OnboardingPopup
                        display={onboardingOpen}
                        onClose={()=>{setOnboardingOpen(false); clearData();}}
                    />

                <HeroCarousel
                    styles={styles}
                    slides={heroSlides}
                    onAction={handleHeroAction}
                    buttonDisabled={true}
                    cmsSlides={cmsContent?.hero ?? undefined}
                />
                <GallerySection styles={styles} cmsImages={cmsContent?.gallery ?? undefined} />
                <LandingOffersSection
                    styles={styles}
                    filterCards={filterCardConfigs.catalog}
                    filterData={filterData}
                    handleFilter={handleFilter}
                    basicFilter={basicFilter}
                    handleBasicFilter={handleBasicFilter}
                    productLayout={productLayout}
                    setProductLayout={setProductLayout}
                    onOpenFilter={() => setFilterOpen(true)}
                    onOpenTimeModal={open}
                    catalogData={catalogData}
                    ProductSectionComponent={ProductSection}
                    loading={loading}
                    showSalesButtons={true}
                    formatPriceRange={(min, max) => `${min} - ${max}kÄŤ`}
                />
                <PartnersSection styles={styles} cmsPartners={cmsContent?.partners} />
                <div className={styles.ActivitySection}>
                    <p className={styles.ActivitySectionTitle}>
                        <Trans i18nKey="home.activities.title" components={{ 1: <span/> }} />
                    </p>
                    <div className={styles.Activities}>
                        {blogPosts.map((post,index)=>(
                            <BlogCard key={`${post.id}-${index}`} id={post.id} title={post.title} imageSrc={post.imageSrc} description={post.description} />
                        ))}
                    </div>
                </div>
            </div>
            <div className={styles.Footer}>
                <Footer/>
            </div>
        </div>
        </>
    )
};

export default Catalog;
